/**
 * Backtracking Queens solver (pure — used by both client and server).
 *
 * Rules (matching validate() in client/game.ts):
 *   - Exactly one queen per row, per column, and per color region.
 *   - No two queens are immediately diagonally adjacent (|dr|=1 && |dc|=1).
 *
 * Returns a list of solutions. Each solution is an array where solution[row] = col.
 * Stops collecting once maxSolutions is reached (default 1000).
 */
export function solve(colorRegions: string[][], maxSolutions = 1000): number[][] {
  const size = colorRegions.length;
  const solutions: number[][] = [];
  const cols = new Array<number>(size);

  const usedCol = new Array<boolean>(size).fill(false);
  const usedColor = new Map<string, boolean>();

  function bt(row: number) {
    if (solutions.length >= maxSolutions) return;
    if (row === size) {
      solutions.push(cols.slice());
      return;
    }
    for (let col = 0; col < size; col++) {
      if (usedCol[col]) continue;
      const color = colorRegions[row][col];
      if (usedColor.get(color)) continue;
      // Check diagonal adjacency against the queen placed in the previous row only.
      // Queens further up can't be immediately diagonal to this row.
      if (row > 0 && Math.abs(cols[row - 1] - col) === 1) continue;

      cols[row] = col;
      usedCol[col] = true;
      usedColor.set(color, true);
      bt(row + 1);
      usedCol[col] = false;
      usedColor.set(color, false);
    }
  }

  bt(0);
  return solutions;
}
